import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Megaphone, ChevronRight, AlertTriangle, Info, Siren, ShieldCheck, Circle } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "./ui/drawer";
import { api } from "../lib/api";
import { useT } from "../lib/lang";
import { userIdentity } from "../lib/identity";

const KEY_READ = "rr_announcements_read"; // localStorage cache of admin broadcasts
const POLL_MS = 30_000;

const readSet = () => {
  try { return new Set(JSON.parse(localStorage.getItem(KEY_READ) || "[]")); }
  catch (_e) { return new Set(); }
};
const writeSet = (set) => {
  try { localStorage.setItem(KEY_READ, JSON.stringify([...set])); } catch (_e) { /* noop */ }
};

const SEV_META = {
  info:     { Icon: Info,           label: "Info",     pill: "bg-sky-100 text-sky-700 border-sky-300",
              stripe: "bg-sky-600" },
  warning:  { Icon: AlertTriangle,  label: "Vigilance", pill: "bg-amber-100 text-amber-800 border-amber-300",
              stripe: "bg-amber-600" },
  critical: { Icon: Siren,          label: "Critique", pill: "bg-rescue-red/15 text-rescue-red border-rescue-red/40",
              stripe: "bg-rescue-red" },
};

// Active diffusions are pinned above regular alerts and, among
// themselves, ranked by severity before recency (Critique > Vigilance > Info).
const SEV_RANK = { critical: 3, warning: 2, info: 1 };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mapPreviewSrc = (path) => {
  if (!path) return "";
  return /^https?:\/\//i.test(path) ? path : `${BACKEND}${path}`;
};

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `il y a ${minutes} min`;
    if (minutes < 60 * 24) return `il y a ${Math.round(minutes / 60)} h`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return iso; }
};

/**
 * AnnouncementBell — small bell icon with unread badge; tap opens a
 * drawer listing every active broadcast.
 *
 * Read state is client-only (localStorage) per product decision:
 *   * No new backend endpoints needed.
 *   * Trade-off: clearing local data resets read flags. Acceptable for
 *     low-friction citizen UX.
 */
export const AnnouncementBell = () => {
  const [t] = useT();
  const [bcs, setBcs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [notifsUnread, setNotifsUnread] = useState(0);
  const [read, setRead] = useState(readSet());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    const phone = userIdentity.getPhone();
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/broadcasts/active");
        if (mounted) setBcs(data || []);
      } catch (_e) { /* silent */ }
      if (phone) {
        try {
          const { data } = await api.get("/notifications", { params: { reporter_phone: phone, limit: 50 } });
          if (mounted) {
            setNotifs(data.items || []);
            setNotifsUnread(data.unread_count || 0);
          }
        } catch (_e) { /* silent */ }
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  // Active diffusions: severity first (critical > warning > info), then newest.
  const orderedBcs = useMemo(() => {
    const items = [...bcs];
    items.sort((a, b) => {
      const rankDiff = (SEV_RANK[b.severity] || 0) - (SEV_RANK[a.severity] || 0);
      if (rankDiff !== 0) return rankDiff;
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return items;
  }, [bcs]);
  const orderedNotifs = useMemo(() => {
    const items = [...notifs];
    items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return items;
  }, [notifs]);

  const bcUnread = orderedBcs.reduce((acc, b) => (read.has(b.id) ? acc : acc + 1), 0);
  const unreadCount = bcUnread + notifsUnread;

  const markRead = (id) => {
    if (read.has(id)) return;
    const next = new Set(read);
    next.add(id);
    setRead(next);
    writeSet(next);
  };

  const markAllRead = async () => {
    const next = new Set(read);
    for (const b of orderedBcs) next.add(b.id);
    setRead(next);
    writeSet(next);
    // Also mark all server-side notifs as read.
    const phone = userIdentity.getPhone();
    if (phone && notifsUnread > 0) {
      try {
        await api.post("/notifications/read-all", null, { params: { reporter_phone: phone } });
        setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
        setNotifsUnread(0);
      } catch (_e) { /* silent — UI was already optimistic */ }
    }
  };

  const markNotifRead = async (notifId) => {
    const phone = userIdentity.getPhone();
    if (!phone) return;
    try {
      await api.post(`/notifications/${notifId}/read`, null, { params: { reporter_phone: phone } });
      setNotifs((prev) => prev.map((n) => (n.id === notifId ? { ...n, read: true } : n)));
      setNotifsUnread((prev) => Math.max(0, prev - 1));
    } catch (_e) { /* silent */ }
  };

  return (
    <>
      <button
        type="button"
        data-testid="announcement-bell-btn"
        onClick={() => setOpen(true)}
        aria-label={`${t.announcements}${unreadCount ? ` · ${unreadCount} ${t.unreadAnnouncement}` : ""}`}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-rescue-line bg-white hover:border-mali-navy hover:text-mali-navy transition-colors shadow-sm"
      >
        <Bell className={`w-4 h-4 ${unreadCount > 0 ? "text-mali-red" : "text-rescue-mute"}`} strokeWidth={2.5} />
        {unreadCount > 0 && (
          <span
            data-testid="announcement-bell-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-mali-red text-white text-[10px] font-bold flex items-center justify-center tabular-nums shadow-md"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent data-testid="announcement-drawer" className="max-h-[85vh]">
          <DrawerHeader className="pb-2 text-left">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute">
                  {t.announcementsKicker}
                </div>
                <DrawerTitle className="text-xl font-black flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-mali-red" /> {t.announcements}
                </DrawerTitle>
                <DrawerDescription className="text-xs text-rescue-mute">
                  {(orderedNotifs.length + orderedBcs.length) === 0
                    ? t.announcementsEmpty
                    : `${orderedNotifs.length + orderedBcs.length} · ${unreadCount} ${t.unreadAnnouncement.toLowerCase()}`}
                </DrawerDescription>
              </div>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  data-testid="announcement-mark-all-read"
                  className="shrink-0 text-[10px] font-bold uppercase tracking-[0.15em] text-mali-navy hover:underline"
                >
                  {t.announcementMarkAllRead}
                </button>
              )}
            </div>
          </DrawerHeader>

          <div className="overflow-y-auto px-3 pb-5">
            {(orderedNotifs.length === 0 && orderedBcs.length === 0) ? (
              <div className="px-4 py-10 text-center text-sm text-rescue-mute" data-testid="announcement-empty">
                {t.announcementsEmpty}
              </div>
            ) : (
              <div className="space-y-4" data-testid="announcement-list">
                {orderedBcs.length > 0 && (
                  <div data-testid="announcement-diffusions-section">
                    <div className="text-[9px] font-bold tracking-[0.25em] uppercase text-rescue-mute mb-1.5 pl-1">
                      Diffusions actives
                    </div>
                    <ul className="space-y-2">
                      {orderedBcs.map((b) => {
                        const sev = SEV_META[b.severity] || SEV_META.info;
                        const { Icon } = sev;
                        const isUnread = !read.has(b.id);
                        return (
                          <li key={b.id}>
                            <Link
                              to={`/broadcasts/${b.id}`}
                              onClick={() => { markRead(b.id); setOpen(false); }}
                              data-testid={`announcement-item-${b.id}`}
                              className={`relative flex gap-2 border-2 rounded-lg bg-white px-3 py-3 hover:border-mali-navy transition-colors ${
                                isUnread ? "border-rescue-ink" : "border-rescue-line"
                              }`}
                            >
                              <span className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md ${sev.stripe}`} aria-hidden />
                              {b.map_preview_url && (
                                <img
                                  src={mapPreviewSrc(b.map_preview_url)}
                                  alt="Zone de la diffusion"
                                  loading="lazy"
                                  data-testid={`announcement-map-preview-${b.id}`}
                                  className="w-14 h-14 shrink-0 object-cover border border-rescue-line rounded"
                                />
                              )}
                              <div className="pl-2 flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] border ${sev.pill}`}>
                                      <Icon className="w-3 h-3" /> {sev.label}
                                    </span>
                                    {isUnread && (
                                      <span className="shrink-0 w-2 h-2 rounded-full bg-mali-red" aria-label={t.unreadAnnouncement} />
                                    )}
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-rescue-mute shrink-0" />
                                </div>
                                <div className="mt-1.5 font-display font-black text-base leading-tight text-rescue-ink line-clamp-2">
                                  {b.title}
                                </div>
                                {b.message && (
                                  <p className="mt-0.5 text-[12px] text-rescue-mute line-clamp-2">{b.message}</p>
                                )}
                                <div className="mt-1.5 font-mono text-[10px] text-rescue-mute">
                                  {t.receivedAt} · {formatTime(b.created_at)}
                                </div>
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {orderedNotifs.length > 0 && (
                  <div data-testid="announcement-alerts-section">
                    <div className="text-[9px] font-bold tracking-[0.25em] uppercase text-rescue-mute mb-1.5 pl-1">
                      Vos alertes
                    </div>
                    <ul className="space-y-2">
                      {orderedNotifs.map((n) => {
                        const isUnread = !n.read;
                        const userFacing = n?.payload?.user_facing_status;
                        const alertId = n?.payload?.alert_id;
                        const isAdminMsg = n.type === "admin_message";
                        const linkTo = isAdminMsg ? "/messages" : (alertId ? `/alerts/${alertId}/status` : null);
                        const stripeColor = isAdminMsg ? "bg-mali-navy" : "bg-emerald-600";
                        const pillCls = isAdminMsg
                          ? "bg-mali-navy/10 text-mali-navy border-mali-navy/30"
                          : "bg-emerald-50 text-emerald-700 border-emerald-300";
                        const Body = (
                          <div className="pl-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] border ${pillCls}`}>
                                  <ShieldCheck className="w-3 h-3" /> {isAdminMsg ? "Message" : "Statut"}
                                </span>
                                {isUnread && (
                                  <span className="shrink-0 w-2 h-2 rounded-full bg-mali-red" aria-label={t.unreadAnnouncement} />
                                )}
                              </div>
                              {linkTo && <ChevronRight className="w-4 h-4 text-rescue-mute shrink-0" />}
                            </div>
                            <div className="mt-1.5 font-display font-black text-base leading-tight text-rescue-ink line-clamp-2">
                              {n.title}
                            </div>
                            {n.body && (
                              <p className="mt-0.5 text-[12px] text-rescue-mute line-clamp-3">{n.body}</p>
                            )}
                            <div className="mt-1.5 font-mono text-[10px] text-rescue-mute">
                              {t.receivedAt} · {formatTime(n.created_at)}
                              {userFacing && <span className="ml-1.5 px-1 py-0.5 bg-rescue-bg uppercase tracking-[0.1em]">· {userFacing}</span>}
                            </div>
                          </div>
                        );
                        const className = `relative block border-2 rounded-lg bg-white px-3 py-3 hover:border-mali-navy transition-colors ${
                          isUnread ? "border-rescue-ink" : "border-rescue-line"
                        }`;
                        if (linkTo) {
                          return (
                            <li key={n.id}>
                              <Link
                                to={linkTo}
                                onClick={() => { markNotifRead(n.id); setOpen(false); }}
                                data-testid={`notif-item-${n.id}`}
                                className={className}
                              >
                                <span className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md ${stripeColor}`} aria-hidden />
                                {Body}
                              </Link>
                            </li>
                          );
                        }
                        return (
                          <li key={n.id}>
                            <button
                              type="button"
                              onClick={() => markNotifRead(n.id)}
                              data-testid={`notif-item-${n.id}`}
                              className={`w-full text-left ${className}`}
                            >
                              <span className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md ${stripeColor}`} aria-hidden />
                              {Body}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default AnnouncementBell;
